import { useEffect, useRef } from 'react';
import { callDeepLink } from '@croquiscom/web2app';

import { useCheckoutRouter } from '@common/app-manager';
import { Toast } from '@common/overlay-manager';
import { convertToPDPDeepLink, convertToWebPDPLink } from '@common/pdp';
import { gotoLogin } from '@common/utils';
import { useOptionsControl } from '@widgets/select-option';
import {
  EstimateShippingInfo,
  openFullScreenSimpleBrowser,
  TabType,
  useCatalogProductIdParams,
  useDocumentVisible,
  useGetPdpIntegratedData,
  useIsAuthenticated,
  usePdpBaseInfo,
  usePdpConfig,
  useZonlyEstimateShippingInfo,
} from '@domains/pdp-common';
import { useFocusTab } from '@domains/pdp-common';

import { useShippingUBL } from '../log/useShippingUBL';

const ReturnFromAddressPage = 'refetchZonlyEstimateShippingInfo';
export const useShippingZigzinProductService = () => {
  const router = useCheckoutRouter();
  const { isZigzagApp } = usePdpConfig();
  const { isLoggedIn } = useIsAuthenticated();
  const { shippingInfoClickUBL, shippingDefaultAddressClickUBL, shippingDefaultAddressImpressionUBL } =
    useShippingUBL();
  const { setFocusTab } = useFocusTab();
  const documentVisible = useDocumentVisible();
  const language = 'ko';

  const params = useCatalogProductIdParams();
  const {
    product: {
      id: product_id,
      product_price: {
        final_discount_info: { discount_price },
      },
      shipping_send_date,
      fulfillment_type,
    },
    shop,
  } = usePdpBaseInfo(params);
  const { displayDeliveryTypeToggleButton } = useOptionsControl();

  const { matching_catalog_product_info } = useGetPdpIntegratedData({
    catalog_product_id: params.catalog_product_id,
  });
  const { pdp_zigzin_shipping_base_info: shippingInfo, refetch: refetchZonlyEstimateShippingInfo } =
    useZonlyEstimateShippingInfo({
      catalog_product_id: params.catalog_product_id,
      final_price: discount_price,
    });
  const firstData = useRef<EstimateShippingInfo | null>(null);
  const isEstimateTimestampExist = shippingInfo?.zigzin_shipping?.order_cutoff_timestamp !== 0;

  useEffect(() => {
    if (firstData.current === null) {
      firstData.current = shippingInfo;
    }
  }, [shippingInfo]);

  useEffect(() => {
    const hasReturnFromAddressPage = sessionStorage.getItem(ReturnFromAddressPage);
    const hasDefaultAddressGuide = Boolean(firstData.current?.default_address_guide);
    const shouldRefetch = documentVisible && hasReturnFromAddressPage && hasDefaultAddressGuide;

    if (shouldRefetch) {
      const shouldShowToast = shippingInfo?.default_address_guide === null;
      refetchZonlyEstimateShippingInfo();

      if (shouldShowToast) {
        Toast.show('등록한 배송지로 배송정보가 적용되었어요');
        sessionStorage.removeItem(ReturnFromAddressPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentVisible, shippingInfo?.default_address_guide]);

  const onClickInfoIcon = () => {
    shippingInfoClickUBL(params.catalog_product_id);
    openFullScreenSimpleBrowser(`/catalog/product/shipping-info?ids=${product_id}&tab=${fulfillment_type}`);
  };

  const handleGoToProductDetail = () => {
    if (!matching_catalog_product_info) {
      return;
    }

    setFocusTab(TabType.CONTACT);

    const url = isZigzagApp
      ? convertToPDPDeepLink({
          browsing_type: matching_catalog_product_info.browsing_type,
          product_url: matching_catalog_product_info.pdp_url,
          shop_id: shop.id,
          shop_product_no: '', //todo 필요한 값인지 확인 필요.
          catalog_product_id: matching_catalog_product_info.id,
        })
      : convertToWebPDPLink({
          product_url: matching_catalog_product_info.pdp_url,
        });
    window.location.assign(url);
  };

  const onClickDefaultAddress = () => {
    shippingDefaultAddressClickUBL();
    if (!isLoggedIn) {
      if (isZigzagApp) {
        callDeepLink('/login')();
      } else {
        gotoLogin({ redirect_url: window.location.href });
      }
    }

    if (isLoggedIn) {
      sessionStorage.setItem(ReturnFromAddressPage, 'true');

      const url = shippingInfo?.default_address_guide?.register_default_address_url ?? '';
      if (isZigzagApp) {
        router.push(url.substring(url.lastIndexOf('/')));
      } else {
        window.open(url, '_blank');
      }
    }
  };

  return {
    displayDeliveryTypeToggleButton,
    shippingInfo,
    itemGuideText: shipping_send_date?.item_guide_text ?? null,
    language,
    isEstimateTimestampExist,
    onClickInfoIcon,
    handleGoToProductDetail,
    refetchZonlyEstimateShippingInfo,
    onClickDefaultAddress,
    shippingDefaultAddressImpressionUBL,
  };
};
